import { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api, ApiFailure } from "../api/client";
import type { Node } from "../api/types";
import { failureMessage } from "../api/useResource";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import {
  ActionError, Badge, Button, Card, ErrorState, Field, Loading, Modal, PageHeader,
  inputClass,
} from "../components/ui";
import { formatDateTime, useInterval } from "./Calls";

// Estado de los nodos Asterisk. Vista de plataforma (superadmin).
//
// Lo que esta vista tiene que contestar es una sola pregunta: ¿hay capacidad
// para originar? Por eso la columna que importa es ocupación frente a
// capacidad, y no la lista de atributos del nodo.
//
// Lo que **no** muestra es el host privado: §6 prohíbe exponerlo, y el
// endpoint no lo devuelve. Se puede escribir uno nuevo al editar, pero no
// leer el que hay.
const POLL_INTERVAL_MS = 10000;

export function Nodes() {
  const [nodes, setNodes] = useState<Node[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Node | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // El aviso de reinicio sobrevive al cierre del formulario a propósito: es lo
  // que queda por hacer, y dentro del modal desaparecería al guardar.
  const [avisoDeReinicio, setAvisoDeReinicio] = useState(false);

  async function cambiarEstado(node: Node, status: Node["status"]) {
    setActionError(null);
    try {
      await api.patch(`/api/v1/nodes/${node.id}`, { status });
      await load(true);
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo cambiar el estado del nodo"));
    }
  }

  const load = useCallback(async (silent: boolean) => {
    if (!silent) {
      setNodes(null);
      setError(null);
    }
    try {
      setNodes(await api.get<Node[]>("/api/v1/nodes"));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
    }
  }, []);

  useEffect(() => {
    void load(false);
  }, [load]);

  // Intervalo fijo y holgado: la ocupación de un nodo no cambia tan rápido
  // como para justificar más, y esta vista la tienen abierta los operadores
  // durante horas.
  useInterval(() => void load(true), POLL_INTERVAL_MS);

  const columnas = useMemo<ColumnDef<Node, any>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Nodo",
        meta: { label: "Nodo" },
        cell: ({ row }) => (
          <>
            <div className="font-medium text-slate-100">{row.original.name}</div>
            <div className="font-mono text-xs text-slate-500">{row.original.id}</div>
          </>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "8rem" },
        cell: ({ row }) => (
          <Badge
            tone={
              row.original.status === "active"
                ? "good"
                : row.original.status === "draining"
                  ? "warn"
                  : "bad"
            }
          >
            {NODE_STATUS_LABELS[row.original.status] ?? row.original.status}
          </Badge>
        ),
      },
      {
        // La pregunta de esta vista es si hay capacidad: se ordena por la
        // proporción, no por el número de llamadas vivas.
        id: "ocupacion",
        accessorFn: (node) => (node.capacity_calls > 0 ? node.active_calls / node.capacity_calls : 0),
        header: "Ocupación",
        meta: { label: "Ocupación" },
        cell: ({ row }) => (
          <Occupancy active={row.original.active_calls} capacity={row.original.capacity_calls} />
        ),
      },
      {
        id: "weight",
        accessorKey: "weight",
        header: "Peso",
        meta: { label: "Peso", align: "right", width: "6rem" },
        cell: ({ row }) => <span className="text-slate-600">{row.original.weight}</span>,
      },
      {
        id: "last_seen_at",
        accessorKey: "last_seen_at",
        header: "Visto por última vez",
        meta: { label: "Visto por última vez", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.last_seen_at)}</span>
        ),
      },
    ],
    [],
  );

  const acciones = useMemo<DataTableRowAction<Node>[]>(
    () => [
      { label: "Editar", onSelect: (node) => setEditando(node) },
      {
        // Drenar es la operación de siempre antes de una ventana: deja de
        // mandarle llamadas nuevas y espera a que terminen las que tiene.
        label: "Drenar",
        hidden: (node) => node.status !== "active",
        onSelect: (node) => void cambiarEstado(node, "draining"),
      },
      {
        label: "Reactivar",
        hidden: (node) => node.status === "active",
        onSelect: (node) => void cambiarEstado(node, "active"),
      },
      {
        label: "Marcar como caído",
        tone: "danger",
        hidden: (node) => node.status === "down",
        disabled: (node) =>
          node.active_calls > 0 && `tiene ${node.active_calls} llamadas en curso`,
        onSelect: (node) => void cambiarEstado(node, "down"),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Nodos Asterisk"
        description="Capacidad y ocupación de cada nodo. Un nodo sin capacidad libre no recibe originaciones."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void load(false)}>
              Actualizar
            </Button>
            {!creando && <Button onClick={() => setCreando(true)}>Registrar nodo</Button>}
          </div>
        }
      />

      {actionError !== null && <ActionError message={actionError} />}

      {avisoDeReinicio && (
        <Card className="mb-3 border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
          Cambiaron datos de la conexión AMI. El nodo seguirá usando los anteriores hasta que
          se reinicie el rol <code>telephony</code>: <code>systemctl restart iamedia-telephony</code>.
          <button
            className="ml-2 underline hover:text-amber-100"
            onClick={() => setAvisoDeReinicio(false)}
          >
            Entendido
          </button>
        </Card>
      )}

      {editando !== null && (
        <EdicionDeNodo
          node={editando}
          onCancel={() => setEditando(null)}
          onSaved={(reinicio) => {
            setEditando(null);
            if (reinicio) setAvisoDeReinicio(true);
            void load(true);
          }}
        />
      )}

      {creando && (
        <FormularioNodo
          onCancel={() => setCreando(false)}
          onCreated={() => {
            setCreando(false);
            void load(true);
          }}
        />
      )}

      {nodes === null && error === null && <Loading />}
      {nodes === null && error !== null && (
        <ErrorState message={error} onRetry={() => void load(false)} />
      )}
      {nodes !== null && error !== null && (
        <Card className="mb-3 border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
          No se pudo actualizar: {error}.
        </Card>
      )}

      {nodes !== null && (
        <DataTable<Node>
          data={nodes}
          columns={columnas}
          getRowId={(node) => node.id}
          caption="Nodos Asterisk"
          empty={{
            title: "Sin nodos",
            description:
              "Sin al menos un nodo activo no se puede originar ninguna llamada, y tampoco se puede registrar una troncal: una troncal cuelga de un nodo.",
          }}
          search={{ placeholder: "Buscar por nombre…" }}
          rowActions={acciones}
          filters={[
            {
              columnId: "status",
              label: "Estado",
              options: [
                { value: "active", label: "Activos" },
                { value: "draining", label: "Drenando" },
                { value: "down", label: "Caídos" },
              ],
            },
          ]}
          urlStateKey="nodos"
        />
      )}
    </div>
  );
}

const NODE_STATUS_LABELS: Record<string, string> = {
  active: "Activo",
  draining: "Drenando",
  down: "Caído",
};

/**
 * Occupancy muestra llamadas vivas frente a capacidad.
 *
 * El umbral de aviso está en el 80 %: es donde una campaña empezando puede
 * agotar el nodo antes de que alguien reaccione, y verlo en rojo solo al 100 %
 * llega tarde — a esas alturas ya hay llamadas rechazadas.
 */
function Occupancy({ active, capacity }: { active: number; capacity: number }) {
  const ratio = capacity > 0 ? active / capacity : 0;
  const tone = ratio >= 1 ? "bg-rose-400" : ratio >= 0.8 ? "bg-amber-400" : "bg-emerald-400";

  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded bg-white/10">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(ratio, 1) * 100}%` }} />
      </div>
      <span className="text-xs text-slate-600">
        {active} / {capacity}
      </span>
    </div>
  );
}

/**
 * FormularioNodo registra un nodo Asterisk.
 *
 * Las credenciales AMI se mandan una vez y el backend las guarda cifradas con
 * `IAMEDIA_SECRETS_KEY`; **no se devuelven nunca**, ni aquí ni al consultar el
 * nodo (CLAUDE.md §6). Que no se puedan leer no quiere decir que no se puedan
 * cambiar: eso lo hace `EdicionDeNodo`, mandando solo lo que se escribe.
 *
 * El host privado es de la red interna a propósito. AMI no se expone a
 * Internet (§8), así que lo normal es `127.0.0.1` cuando la plataforma corre
 * en el mismo servidor que Asterisk.
 */
/**
 * EdicionDeNodo cambia un nodo ya registrado.
 *
 * # Por qué los datos de conexión llegan vacíos
 *
 * Porque la API **no los devuelve nunca**: ni el host privado, ni el usuario,
 * ni el secreto (§6). Así que el formulario no puede mostrarlos, y lo que hace
 * es lo honesto: dejarlos en blanco y decir que en blanco significa «como
 * estaban». Rellenarlos con un valor inventado para que «se vea algo» sería
 * peor —al guardar escribiría ese invento—.
 *
 * # Por qué el aviso de reinicio
 *
 * La capacidad, el peso y el estado los lee la selección de nodo en cada
 * originación: surten efecto en la siguiente llamada. Las credenciales AMI las
 * lee el gestor de nodos al arrancar, así que cambiarlas no reconecta sola.
 * Sin decirlo, alguien corrige una contraseña, ve «guardado» y se queda con un
 * nodo que sigue sin conectar.
 */
function EdicionDeNodo({
  node,
  onCancel,
  onSaved,
}: {
  node: Node;
  onCancel: () => void;
  onSaved: (reinicioHaceFalta: boolean) => void;
}) {
  const [name, setName] = useState(node.name);
  const [status, setStatus] = useState(node.status);
  const [capacity, setCapacity] = useState(String(node.capacity_calls));
  const [weight, setWeight] = useState(String(node.weight));
  const [privateHost, setPrivateHost] = useState("");
  const [amiPort, setAmiPort] = useState("");
  const [amiUsername, setAmiUsername] = useState("");
  const [amiSecret, setAmiSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Solo lo que cambió: un PATCH con todos los campos reescribiría datos
      // que nadie tocó, y en los de conexión reescribiría con el vacío.
      const cambios: Record<string, unknown> = {};
      if (name.trim() !== node.name) cambios.name = name.trim();
      if (status !== node.status) cambios.status = status;
      if (Number(capacity) !== node.capacity_calls) cambios.capacity_calls = Number(capacity);
      if (Number(weight) !== node.weight) cambios.weight = Number(weight);
      if (privateHost.trim() !== "") cambios.private_host = privateHost.trim();
      if (amiPort.trim() !== "") cambios.ami_port = Number(amiPort);
      if (amiUsername.trim() !== "") cambios.ami_username = amiUsername.trim();
      if (amiSecret !== "") cambios.ami_secret = amiSecret;

      if (Object.keys(cambios).length === 0) {
        onCancel();
        return;
      }
      const actualizado = await api.patch<Node & { connection_restart_required?: boolean }>(
        `/api/v1/nodes/${node.id}`,
        cambios,
      );
      onSaved(actualizado.connection_restart_required === true);
    } catch (err) {
      setError(failureMessage(err, "no se pudo guardar el nodo"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Editar «${node.name}»`}
      description="Capacidad, reparto y estado surten efecto en la siguiente llamada."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field label="Nombre">
          <input
            className={inputClass}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Estado"
            hint="«Drenando» deja de mandarle llamadas nuevas; las que están en curso siguen."
          >
            <select
              className={inputClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as Node["status"])}
            >
              <option value="active">Activo</option>
              <option value="draining">Drenando</option>
              <option value="down">Caído</option>
            </select>
          </Field>
          <Field label="Capacidad" hint="Llamadas simultáneas.">
            <input
              className={inputClass}
              type="number"
              min={1}
              required
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </Field>
          <Field label="Peso" hint="Cuánto le toca frente a los demás nodos.">
            <input
              className={inputClass}
              type="number"
              min={1}
              required
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
          </Field>
        </div>

        <details className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <summary className="cursor-pointer text-sm text-slate-300">
            Conexión AMI
            <span className="ml-2 text-xs text-slate-500">
              en blanco = sin cambios · reiniciar telephony para que aplique
            </span>
          </summary>

          <p className="mt-3 text-xs text-slate-500">
            La plataforma no devuelve el host, el usuario ni el secreto de un nodo, así que
            estos campos no pueden venir rellenos. Lo que se escriba aquí sustituye a lo que
            hubiera; lo que se deje vacío se queda como está.
          </p>

          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Field label="Host privado">
              <input
                className={`${inputClass} font-mono`}
                placeholder="sin cambios"
                value={privateHost}
                onChange={(e) => setPrivateHost(e.target.value)}
              />
            </Field>
            <Field label="Puerto AMI">
              <input
                className={inputClass}
                type="number"
                min={1}
                max={65535}
                placeholder="sin cambios"
                value={amiPort}
                onChange={(e) => setAmiPort(e.target.value)}
              />
            </Field>
            <Field label="Usuario AMI">
              <input
                className={`${inputClass} font-mono`}
                placeholder="sin cambios"
                value={amiUsername}
                onChange={(e) => setAmiUsername(e.target.value)}
              />
            </Field>
            <Field label="Secreto AMI" hint="Mínimo 16 caracteres.">
              <input
                className={inputClass}
                type="password"
                autoComplete="off"
                placeholder="sin cambios"
                value={amiSecret}
                onChange={(e) => setAmiSecret(e.target.value)}
              />
            </Field>
          </div>
        </details>

        {error !== null && <ActionError message={error} />}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function FormularioNodo({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [privateHost, setPrivateHost] = useState("127.0.0.1");
  const [amiPort, setAmiPort] = useState("5038");
  const [amiUsername, setAmiUsername] = useState("iamedia");
  const [amiSecret, setAmiSecret] = useState("");
  const [fastagiHost, setFastagiHost] = useState("127.0.0.1");
  const [fastagiPort, setFastagiPort] = useState("4574");
  const [capacity, setCapacity] = useState("50");
  const [weight, setWeight] = useState("1");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/nodes", {
        name,
        private_host: privateHost,
        ami_port: Number(amiPort),
        ami_username: amiUsername,
        ami_secret: amiSecret,
        fastagi_host: fastagiHost,
        fastagi_port: Number(fastagiPort),
        capacity_calls: Number(capacity),
        weight: Number(weight),
      });
      onCreated();
    } catch (err) {
      setError(failureMessage(err, "no se pudo registrar el nodo"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Registrar un nodo Asterisk"
      description="Los valores por omisión sirven para un Asterisk en la misma máquina."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field label="Nombre" hint="Para reconocerlo en reportes y alertas.">
          <input
            className={inputClass}
            required
            placeholder="pbxcac"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Host privado"
            hint="En la red interna, nunca accesible desde Internet. Si la plataforma corre en el mismo servidor que Asterisk, 127.0.0.1."
          >
            <input
              className={`${inputClass} font-mono`}
              required
              value={privateHost}
              onChange={(e) => setPrivateHost(e.target.value.trim())}
            />
          </Field>
          <Field label="Puerto AMI">
            <input
              className={inputClass}
              type="number"
              min={1}
              max={65535}
              required
              value={amiPort}
              onChange={(e) => setAmiPort(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Usuario AMI" hint="El de manager.conf, con permisos de originate y call.">
            <input
              className={`${inputClass} font-mono`}
              required
              value={amiUsername}
              onChange={(e) => setAmiUsername(e.target.value.trim())}
            />
          </Field>
          <Field
            label="Secreto AMI"
            hint="Se guarda cifrado y no se vuelve a mostrar. Se puede sustituir después al editar el nodo."
          >
            <input
              className={inputClass}
              type="password"
              required
              autoComplete="off"
              value={amiSecret}
              onChange={(e) => setAmiSecret(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Host FastAGI"
            hint="A dónde abre el nodo la sesión AGI. Tiene que coincidir con el dialplan instalado."
          >
            <input
              className={`${inputClass} font-mono`}
              required
              value={fastagiHost}
              onChange={(e) => setFastagiHost(e.target.value.trim())}
            />
          </Field>
          <Field label="Puerto FastAGI" hint="4573 suele estar ocupado por otro FastAGI; por eso 4574.">
            <input
              className={inputClass}
              type="number"
              min={1}
              max={65535}
              required
              value={fastagiPort}
              onChange={(e) => setFastagiPort(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Capacidad de llamadas"
            hint="Llamadas simultáneas que aguanta este nodo. Pasarse no da más capacidad: da llamadas con audio malo."
          >
            <input
              className={inputClass}
              type="number"
              min={1}
              required
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </Field>
          <Field label="Peso" hint="Con varios nodos, cuánto le toca a este frente a los demás.">
            <input
              className={inputClass}
              type="number"
              min={1}
              required
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
          </Field>
        </div>

        {error !== null && <ActionError message={error} />}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Registrando…" : "Registrar nodo"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
